/**
 * Hook do zarządzania danymi dla WiseChart
 */

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'query-string';
import { isEqual } from 'lodash';
import { mapValues, keyBy } from 'lodash';
import { useLoadData } from 'plugins/api/endpoints/useLoadData';
import { loadDataUrlParser } from 'plugins/util/parser';
import { usePrevious } from 'hooks';

import { WiseQueryParams, IWiseChartParameter } from 'components/wise/types';
import { PERIOD_PARAM } from 'components/wise/WiseRangeButton/WiseRangeButton';
import { ILoadDataParam } from 'plugins/api/types';

interface IUseWiseChartDataParams {
  loadDataParams?: ILoadDataParam;
  requestParams?: object;
  parameters?: IWiseChartParameter[];
}

interface IUseWiseChartDataReturn {
  chartData: any;
  queryParams: WiseQueryParams;
  setQueryParams: React.Dispatch<React.SetStateAction<WiseQueryParams>>;
  refetchChartData: () => void;
  isLoading: boolean;
}

/**
 * Hook do zarządzania danymi dla WiseChart
 */
export const useWiseChartData = ({
  loadDataParams,
  requestParams,
  parameters
}: IUseWiseChartDataParams): IUseWiseChartDataReturn => {
  const { search } = useLocation();

  // Parsowanie URL z loadDataParams
  const loadDataUrl = loadDataUrlParser(loadDataParams?.get_url || '', {
    ...requestParams,
    ...qs.parse(search)
  });

  const currentGetUrl = loadDataParams?.get_url;
  const prevGetUrl = usePrevious(loadDataParams?.get_url);
  const prevLoadDataUrl = usePrevious(loadDataUrl);

  // Inicjalizacja queryParams z wartościami domyślnymi (date_range: domyślnie period=year, bez _from/_to)
  const [queryParams, setQueryParamsState] = useState<WiseQueryParams>(() => {
    const paramsWithoutDateRange = parameters?.filter((p) => p.type !== 'date_range') ?? [];
    const defaultParams: WiseQueryParams = {
      ...(paramsWithoutDateRange.length
        ? mapValues(keyBy(paramsWithoutDateRange, 'field_symbol'), 'default_value')
        : {})
    };
    // Dla date_range: domyślnie zaznaczony „Rok” – ustawiamy tylko period, bez dat
    if (parameters?.some((p) => p.type === 'date_range')) {
      defaultParams[PERIOD_PARAM] = 'year';
    }
    return defaultParams;
  });

  // Wrapper dla setQueryParams
  const setQueryParams: React.Dispatch<React.SetStateAction<WiseQueryParams>> = React.useCallback(
    (updater) => {
      setQueryParamsState((prevState) => {
        return typeof updater === 'function' ? updater(prevState) : updater;
      });
    },
    []
  );

  const prevQueryParams = usePrevious(queryParams);

  // Pobieranie danych z API
  const {
    data: chartDataResponse,
    isLoading,
    refetch: refetchChartData
  } = useLoadData(loadDataUrl, queryParams, {
    enabled: false,
    cacheTime: 1,
    keepPreviousData: false
  });

  // Automatyczne odświeżanie danych przy zmianie queryParams, loadDataParams lub requestParams (np. clientId)
  useEffect(() => {
    const urlChanged = loadDataUrl && loadDataUrl !== prevLoadDataUrl;
    const getUrlOrQueryChanged =
      !isEqual(currentGetUrl, prevGetUrl) || !isEqual(queryParams, prevQueryParams);
    if (loadDataUrl && (urlChanged || getUrlOrQueryChanged)) {
      refetchChartData();
    }
  }, [
    currentGetUrl,
    prevGetUrl,
    queryParams,
    prevQueryParams,
    loadDataUrl,
    prevLoadDataUrl,
    refetchChartData
  ]);

  return {
    chartData: chartDataResponse,
    queryParams,
    setQueryParams,
    refetchChartData,
    isLoading
  };
};
